import { apiClient } from "@/lib/api-client";

const API_BASE = "/api/v1/portfolio";

export type PortfolioTemplate =
	| "template_a"
	| "template_b"
	| "template_c"
	| "template_d";

export interface PortfolioProject {
	id?: string;
	title: string;
	shortDescription: string;
	fullDescription?: string;
	category?: string;
	completionDate?: string;
	thumbnailUrl: string;
	imageUrls: string[];
}

export type PortfolioReviewStatus =
	| "draft"
	| "pending_review"
	| "approved"
	| "rejected";

export interface DesignerPortfolio {
	id: string;
	/** The designer's name for this portfolio. Null until they have set one. */
	title: string | null;
	template: PortfolioTemplate;
	isPublished: boolean;
	reviewStatus: PortfolioReviewStatus;
	/** The admin's note. The server sends null unless the portfolio is rejected. */
	reviewNotes: string | null;
	slug?: string;
}

export interface MyPortfolioResponse {
	portfolio: DesignerPortfolio;
	projects: PortfolioProject[];
	designer: {
		id: string;
		name: string;
		profilePictureUrl?: string;
		bio?: string;
		isVerified: boolean;
	};
}

export interface PublicPortfolioResponse {
	portfolio: {
		id: string;
		template: PortfolioTemplate;
		isPublished: boolean;
		slug?: string;
	};
	designer: {
		id: string;
		name: string;
		firmName?: string;
		bio?: string;
		profilePictureUrl?: string;
		isVerified: boolean;
	};
	projects: Array<{
		id: string;
		title: string;
		shortDescription: string;
		thumbnailUrl: string;
	}>;
}

export interface PublicPortfolioProjectResponse {
	previewOnly: boolean;
	project: {
		id: string;
		title: string;
		shortDescription: string;
		thumbnailUrl: string;
		fullDescription?: string;
		category?: string;
		completionDate?: string;
		imageUrls?: string[];
	};
}

export async function getMyPortfolio(
	portfolioId?: string,
): Promise<MyPortfolioResponse | null> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/me${portfolioId ? `?portfolioId=${portfolioId}` : ""}`,
		);
		return response.data.data;
	} catch {
		return null;
	}
}

export async function saveMyPortfolio(
	payload: {
		/** Trimmed; an empty string clears the stored title. */
		title?: string;
		template: PortfolioTemplate;
		projects: PortfolioProject[];
	},
	portfolioId?: string,
): Promise<{
	success: boolean;
	portfolio?: DesignerPortfolio;
	projects?: PortfolioProject[];
	error?: string;
}> {
	try {
		const response = await apiClient.put(
			`${API_BASE}/me${portfolioId ? `?portfolioId=${portfolioId}` : ""}`,
			payload,
		);
		return {
			success: true,
			portfolio: response.data.data.portfolio,
			projects: response.data.data.projects,
		};
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to save portfolio",
		};
	}
}

export async function publishMyPortfolio(portfolioId?: string): Promise<{
	success: boolean;
	portfolio?: DesignerPortfolio;
	shareUrlPath?: string;
	error?: string;
}> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/me/publish${portfolioId ? `?portfolioId=${portfolioId}` : ""}`,
		);
		return {
			success: true,
			portfolio: response.data.data.portfolio,
			shareUrlPath: response.data.data.shareUrlPath,
		};
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to publish portfolio",
		};
	}
}

export async function unpublishMyPortfolio(portfolioId?: string): Promise<{
	success: boolean;
	portfolio?: DesignerPortfolio;
	error?: string;
}> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/me/unpublish${portfolioId ? `?portfolioId=${portfolioId}` : ""}`,
		);
		return { success: true, portfolio: response.data.data.portfolio };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to unpublish portfolio",
		};
	}
}

export async function getPublicPortfolio(
	slugOrToken: string,
): Promise<PublicPortfolioResponse | null> {
	try {
		const response = await apiClient.get(`${API_BASE}/${slugOrToken}`);
		return response.data.data;
	} catch {
		return null;
	}
}

export async function getPublicPortfolioProject(
	slugOrToken: string,
	projectId: string,
): Promise<PublicPortfolioProjectResponse | null> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/${slugOrToken}/projects/${projectId}`,
		);
		return response.data.data;
	} catch {
		return null;
	}
}

// ============================================================================
// PHASE 2 — MULTIPLE PORTFOLIOS API
// ============================================================================

export interface ListMyPortfoliosResponse {
	designer: { id: string; name: string };
	portfolios: Array<
		DesignerPortfolio & {
			isPrimary: boolean;
			createdAt: string;
		}
	>;
}

export async function listMyPortfolios(): Promise<ListMyPortfoliosResponse | null> {
	try {
		const response = await apiClient.get(`${API_BASE}/my-portfolios`);
		return response.data.data;
	} catch {
		return null;
	}
}

export async function createPortfolio(payload: {
	title?: string;
	template?: PortfolioTemplate;
}): Promise<{
	success: boolean;
	portfolio?: DesignerPortfolio;
	error?: string;
}> {
	try {
		const response = await apiClient.post(`${API_BASE}`, payload);
		return { success: true, portfolio: response.data.data };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to create portfolio",
		};
	}
}

export async function setPrimaryPortfolio(portfolioId: string): Promise<{
	success: boolean;
	portfolio?: DesignerPortfolio;
	error?: string;
}> {
	try {
		const response = await apiClient.patch(
			`${API_BASE}/${portfolioId}/primary`,
		);
		return { success: true, portfolio: response.data.data };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to set primary portfolio",
		};
	}
}

export async function deletePortfolio(portfolioId: string): Promise<{
	success: boolean;
	error?: string;
}> {
	try {
		await apiClient.delete(`${API_BASE}/${portfolioId}`);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to delete portfolio",
		};
	}
}
